import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

declare global {
  interface Window {
    Razorpay: any;
  }
}

type ProductType = "demo" | "monthly" | "3month" | "6month" | "yearly";

interface RazorpayOptions {
  productType: ProductType;
  planName: string;
  couponCode?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  mt5Id?: string;
  onSuccess?: () => void;
}

const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const getFunctionErrorMessage = async (
  error: unknown,
  data: { error?: string } | null | undefined,
  fallback = "Failed to create order"
): Promise<string> => {
  if (data?.error) return data.error;
  if (!error) return fallback;

  const err = error as { message?: string; context?: Response };

  if (err.context && typeof err.context.clone === "function") {
    try {
      const cloned = err.context.clone();
      const parsed = await cloned.json().catch(async () => ({ error: await cloned.text() }));

      if (parsed && typeof parsed === "object" && "error" in parsed && parsed.error) {
        return String(parsed.error);
      }
    } catch {
      // no-op
    }
  }

  return err.message || fallback;
};

export const useRazorpay = () => {
  const [loading, setLoading] = useState(false);

  const initiatePayment = useCallback(async ({
    productType, planName, couponCode, customerName, customerEmail, customerPhone, mt5Id, onSuccess,
  }: RazorpayOptions) => {
    setLoading(true);

    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast({ title: "Error", description: "Failed to load payment gateway. Please try again.", variant: "destructive" });
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;

      const finalEmail = customerEmail || user?.email || "";
      const finalName = customerName || "";
      const finalPhone = customerPhone || "";

      const response = await supabase.functions.invoke("razorpay-create-order", {
        body: {
          product_type: productType,
          coupon_code: couponCode,
          customer_email: finalEmail,
          customer_name: finalName,
          customer_phone: finalPhone,
          mt5_id: mt5Id || "",
        },
      });

      const data = response.data as { order_id?: string; amount?: number; currency?: string; key_id?: string; error?: string } | null;
      const error = response.error;

      console.log("[Razorpay] Create order response:", { data, error: error?.message || error });

      if (error || !data?.order_id) {
        const errMsg = await getFunctionErrorMessage(error, data, "Failed to create payment order");
        console.error("[Razorpay] Order creation failed:", errMsg, { error, data });
        toast({ title: "Error", description: errMsg, variant: "destructive" });
        setLoading(false);
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "NivexA FX",
        description: planName,
        order_id: data.order_id,
        prefill: {
          name: finalName,
          email: finalEmail,
          contact: finalPhone,
        },
        notes: {
          plan: planName,
          product_type: productType,
          mt5_id: mt5Id || "",
          coupon_code: couponCode || "",
          ...(user ? { user_id: user.id } : {}),
        },
        theme: {
          color: "#00d4ff",
          backdrop_color: "rgba(0,0,0,0.7)",
        },
        modal: {
          ondismiss: () => setLoading(false),
          confirm_close: true,
          escape: true,
          animation: true,
        },
        handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke("razorpay-verify-payment", {
              body: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                product_type: productType,
                customer_email: customerEmail || user?.email || "",
              },
            });

            if (verifyError || !verifyData?.success) {
              toast({ title: "Verification Failed", description: verifyData?.error || "Payment could not be verified. Contact support.", variant: "destructive" });
            } else {
              toast({ title: "Payment Successful! 🎉", description: "Please check your email for next steps." });
              onSuccess?.();
            }
          } catch {
            toast({ title: "Error", description: "Payment verification failed. Please contact support.", variant: "destructive" });
          } finally {
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response: any) => {
        toast({ title: "Payment Failed", description: response.error?.description || "Something went wrong. Please try again.", variant: "destructive" });
        setLoading(false);
      });
      rzp.open();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Something went wrong.", variant: "destructive" });
      setLoading(false);
    }
  }, []);

  return { initiatePayment, loading };
};